import { ChangeDetectionStrategy, Component } from '@angular/core';
import { IconComponent } from '@padspin/ui-marketing-icons';

interface PressEntry {
  name: string;
  quote: string;
  url: string;
}
interface ReviewEntry {
  name: string;
  initial: string;
  text: string;
}

const PRESS: PressEntry[] = [
  {
    name: 'New York Times',
    quote:
      'Padspin circumvents traditional brokers, reducing the fee to incoming tenants.',
    url: 'https://www.nytimes.com/2015/12/06/realestate/new-apps-and-services-for-renters.html',
  },
  {
    name: 'Wall Street Journal',
    quote: 'Padspin bypasses brokers entirely.',
    url: 'https://www.wsj.com/articles/startups-crash-real-estate-brokerage-market-1456223401',
  },
  {
    name: 'Bloomberg',
    quote: 'A breakthrough in rental automation — landlords love Padspin.',
    url: 'https://www.bloomberg.com/news/articles/2016-08-17/can-tech-tools-make-apartment-hunting-in-new-york-affordable',
  },
];

const REVIEWS: ReviewEntry[] = [
  {
    name: 'James R.',
    initial: 'J',
    text: "I used to spend weeks finding a tenant. Padspin found me a great renter in just 72 hours. It's a game-changer.",
  },
  {
    name: 'Sarah L.',
    initial: 'S',
    text: 'The AI pre-qualification process is amazing. I only showed my apartment to serious renters.',
  },
  {
    name: 'Tom D.',
    initial: 'T',
    text: 'Padspin handled everything from listing to lease. Incredibly efficient and stress-free. Highly recommend!',
  },
];

@Component({
  selector: 'padspin-marketing-testimonials-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <section id="press" class="py-24 sm:py-32">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="mx-auto max-w-2xl text-center">
          <p
            class="text-sm font-semibold uppercase tracking-wider text-brand-green-700"
          >
            Press &amp; Reviews
          </p>
          <h2
            class="mt-3 text-4xl font-bold tracking-tight text-color sm:text-5xl"
          >
            Trusted by landlords
            <span class="text-gradient-brand">and the press.</span>
          </h2>
        </div>

        <div class="mt-12 grid gap-4 sm:grid-cols-3">
          @for (p of press; track p.name) {
            <a
              href="{{ p.url }}"
              target="_blank"
              rel="noopener noreferrer"
              class="block rounded-2xl border border-surface bg-gradient-soft p-6 shadow-card transition-smooth hover:shadow-glow"
            >
              <padspin-icon
                name="quote"
                [size]="24"
                class="text-brand-blue-700"
              />
              <p class="mt-3 text-sm italic text-color">"{{ p.quote }}"</p>
              <div class="mt-4 text-sm font-bold text-color">
                {{ p.name }}
              </div>
            </a>
          }
        </div>

        <div class="mt-6 grid gap-4 sm:grid-cols-3">
          @for (r of reviews; track r.name) {
            <div
              class="rounded-2xl border border-surface bg-surface-0 dark:bg-surface-900 p-6 shadow-card transition-smooth hover:shadow-glow"
            >
              <div class="flex gap-1 text-brand-green-700">
                @for (s of stars; track s) {
                  <padspin-icon name="star" [size]="16" class="fill-current" />
                }
              </div>
              <p class="mt-3 text-sm text-color">"{{ r.text }}"</p>
              <div class="mt-4 flex items-center gap-3">
                <div
                  class="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-sm font-bold text-primary-contrast"
                >
                  {{ r.initial }}
                </div>
                <div class="text-sm font-semibold text-color">
                  {{ r.name }}
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class MarketingTestimonialsSectionComponent {
  protected readonly press = PRESS;
  protected readonly reviews = REVIEWS;
  protected readonly stars = [0, 1, 2, 3, 4];
}
